import { useContext, useEffect, useMemo, useRef, useState } from "react"
import firebase from "firebase"
import moment from "moment"
import { db } from "../../../../utils/firebase"
import { AuthContext } from "../../../../utils/providers/auth"
import { AlertsContext } from "../../../../utils/providers/alerts"

import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Button from "../../../../components/ui/button/button"
import Table from "../../../../components/structure/table/table"
import Badge from "../../../../components/ui/badge/badge"

import {
	EXPOSURE_DAYS,
	applyStagedMoves,
	buildBookingDemand,
	buildDayExposure,
	buildExposureMetrics,
	getMoveValidation,
	getCheckInDatesString,
	buildMoveSuggestions,
	formatDayLabel,
	summarizeExposure,
} from "./ticket-exposure.utils"

const DEFAULT_INVENTORY = EXPOSURE_DAYS.reduce((acc, day) => {
	acc[day] = 0
	return acc
}, {})

function normalizeInventory(inventory = {}) {
	return EXPOSURE_DAYS.reduce((acc, day) => {
		acc[day] = Math.max(0, Number(inventory?.[day]) || 0)
		return acc
	}, {})
}

function getStatusBadge(dayExposure) {
	if ((dayExposure?.needToBuy || 0) > 0) {
		return { type: "NEGATIVE", label: `Buy ${dayExposure.needToBuy}` }
	}

	if ((dayExposure?.surplus || 0) > 0) {
		return { type: "POSITIVE", label: `Surplus ${dayExposure.surplus}` }
	}

	return { type: "AMBER", label: "Balanced" }
}

function getInventoryNote(savedInventoryState) {
	if (!savedInventoryState?.updatedAt?.seconds) return "Not saved yet"
	return `Last saved ${moment(savedInventoryState.updatedAt.seconds, "X").fromNow()}`
}

function getMetricCardStyle(tone) {
	switch (tone) {
		case "danger":
			return {
				border: "1px solid #f2c7c3",
				background: "linear-gradient(180deg, #fff8f7 0%, #ffffff 100%)",
				accent: "#cb5a4d",
			}
		case "warning":
			return {
				border: "1px solid #eadcc0",
				background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)",
				accent: "#a97415",
			}
		case "success":
			return {
				border: "1px solid #cfe6d8",
				background: "linear-gradient(180deg, #f7fcf8 0%, #ffffff 100%)",
				accent: "#2d7a4b",
			}
		default:
			return {
				border: "1px solid #e7eaef",
				background: "linear-gradient(180deg, #fbfcfd 0%, #ffffff 100%)",
				accent: "#202938",
			}
	}
}

function buildWarningMessage(message = "", reasons = []) {
	const reasonMessages = reasons.map((reason) => reason?.message).filter(Boolean)

	if (!message) return reasonMessages.join(" ")
	if (reasonMessages.length === 0) return message

	return `${message} ${reasonMessages.join(" ")}`
}

function WarningPanel({ message = "", reasons = [] }) {
	return (
		<div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
			<div style={{ flex: "0 0 auto", alignSelf: "flex-start" }}>
				<Badge
					type="AMBER"
					label="Warning"
				/>
			</div>
			<small style={{ color: "#8a5b06", lineHeight: 1.45, minWidth: 0 }}>{buildWarningMessage(message, reasons)}</small>
		</div>
	)
}

export default function TicketExposure({ bookings, guests, eventId }) {
	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)

	const [loadingInventory, setLoadingInventory] = useState(true)
	const [savingInventory, setSavingInventory] = useState(false)
	const [inventory, setInventory] = useState(DEFAULT_INVENTORY)
	const [savedInventoryState, setSavedInventoryState] = useState({
		values: DEFAULT_INVENTORY,
		updatedAt: null,
		updatedBy: "",
	})
	const [eventConfig, setEventConfig] = useState({
		week_start: "",
		golf_days: [],
	})

	const [selectedBookingID, setSelectedBookingID] = useState("")
	const [selectedFromDay, setSelectedFromDay] = useState("")
	const [selectedToDay, setSelectedToDay] = useState("")
	const [stagedMoves, setStagedMoves] = useState([])
	const [bookingSearch, setBookingSearch] = useState("")
	const [showBookingDropdown, setShowBookingDropdown] = useState(false)
	const bookingSearchRef = useRef(null)

	useEffect(() => {
		let mounted = true

		const loadInventory = async () => {
			if (!eventId) return

			try {
				setLoadingInventory(true)
				const eventDoc = await db.doc(`events/${eventId}`).get()
				const savedInventory = normalizeInventory(eventDoc.data()?.ticket_exposure_inventory || {})
				const updatedAt = eventDoc.data()?.ticket_exposure_inventory?.updated_at || null
				const updatedBy = eventDoc.data()?.ticket_exposure_inventory?.updated_by || ""

				if (!mounted) return

				setInventory(savedInventory)
				setSavedInventoryState({
					values: savedInventory,
					updatedAt,
					updatedBy,
				})
				setEventConfig({
					week_start: eventDoc.data()?.week_start || "",
					golf_days: Array.isArray(eventDoc.data()?.golf_days) ? eventDoc.data().golf_days : [],
				})
			} catch (error) {
				console.error("Failed to load ticket exposure inventory:", error)
				pushAlert({
					type: "ERROR",
					title: "Inventory Load Failed",
					body: "Could not load the saved ticket exposure inventory",
				})
			} finally {
				if (mounted) setLoadingInventory(false)
			}
		}

		loadInventory()

		return () => {
			mounted = false
		}
	}, [eventId, pushAlert])

	const baseBookingDemand = useMemo(() => buildBookingDemand({ bookings, guests }), [bookings, guests])
	const simulatedBookingDemand = useMemo(() => applyStagedMoves({ bookingDemand: baseBookingDemand, stagedMoves }), [baseBookingDemand, stagedMoves])
	const displayedBookingDemand = stagedMoves.length > 0 ? simulatedBookingDemand : baseBookingDemand

	const baseExposure = useMemo(() => buildDayExposure({ bookingDemand: baseBookingDemand, inventory }), [baseBookingDemand, inventory])
	const simulatedExposure = useMemo(() => buildDayExposure({ bookingDemand: simulatedBookingDemand, inventory }), [simulatedBookingDemand, inventory])
	const displayedExposure = stagedMoves.length > 0 ? simulatedExposure : baseExposure

	const baseSummary = useMemo(() => summarizeExposure(baseExposure), [baseExposure])
	const displayedSummary = useMemo(() => summarizeExposure(displayedExposure), [displayedExposure])
	const exposureMetrics = useMemo(
		() =>
			buildExposureMetrics({
				displayedExposure,
				baseExposure,
				displayedSummary,
				baseSummary,
			}),
		[displayedExposure, baseExposure, displayedSummary, baseSummary],
	)

	const bookingOptions = useMemo(() => {
		return displayedBookingDemand.map((booking) => {
			const daysLabel = booking.activeDays.map((day) => `${formatDayLabel(day)} (${booking.days[day]})`).join(", ")
			const reference = String(booking.reference || "").trim()
			const clientName = String(booking.client?.name || "Unknown client").trim()

			return {
				id: booking.bookingID,
				label: `${reference} | ${clientName} | ${daysLabel || "No days"}`,
				searchLabel: `${reference} ${clientName}`.toLowerCase(),
			}
		})
	}, [displayedBookingDemand])
	const filteredBookingOptions = useMemo(() => {
		const searchValue = bookingSearch.trim().toLowerCase()
		if (!searchValue) return bookingOptions
		return bookingOptions.filter((booking) => booking.searchLabel.includes(searchValue))
	}, [bookingOptions, bookingSearch])

	const selectedBooking = useMemo(() => displayedBookingDemand.find((booking) => booking.bookingID === selectedBookingID) || null, [displayedBookingDemand, selectedBookingID])
	const selectedMoveValidation = useMemo(() => {
		if (!selectedBooking || !selectedToDay) return { valid: true, reasons: [] }
		return getMoveValidation({
			booking: selectedBooking,
			targetDay: selectedToDay,
			eventConfig,
		})
	}, [selectedBooking, selectedToDay, eventConfig])

	const sourceDayOptions = useMemo(() => {
		if (!selectedBooking) return {}
		return selectedBooking.activeDays.reduce((acc, day) => {
			acc[day] = `${formatDayLabel(day)} (${selectedBooking.days[day]})`
			return acc
		}, {})
	}, [selectedBooking])

	const targetDayOptions = useMemo(() => {
		return EXPOSURE_DAYS.filter((day) => day !== selectedFromDay).reduce((acc, day) => {
			acc[day] = formatDayLabel(day)
			return acc
		}, {})
	}, [selectedFromDay])

	const suggestions = useMemo(
		() =>
			buildMoveSuggestions({
				bookingDemand: displayedBookingDemand,
				inventory,
				eventConfig,
				limit: 12,
			}),
		[displayedBookingDemand, inventory, eventConfig],
	)
	const bookingDetailsByID = useMemo(
		() =>
			displayedBookingDemand.reduce((acc, booking) => {
				acc[booking.bookingID] = booking
				return acc
			}, {}),
		[displayedBookingDemand],
	)

	useEffect(() => {
		if (!selectedBookingID) return

		const selectedOption = bookingOptions.find((booking) => booking.id === selectedBookingID)
		if (selectedOption) setBookingSearch(selectedOption.label)
	}, [selectedBookingID, bookingOptions])

	useEffect(() => {
		const handleOutsideClick = (event) => {
			if (bookingSearchRef.current && !bookingSearchRef.current.contains(event.target)) {
				setShowBookingDropdown(false)
			}
		}

		document.addEventListener("mousedown", handleOutsideClick)
		return () => document.removeEventListener("mousedown", handleOutsideClick)
	}, [])

	const addMoveToSimulation = (move) => {
		if (!move?.bookingID || !move?.fromDay || !move?.toDay) return

		const duplicate = stagedMoves.some((stagedMove) => stagedMove.bookingID === move.bookingID && stagedMove.fromDay === move.fromDay && stagedMove.toDay === move.toDay)
		if (duplicate) {
			pushAlert({
				type: "ERROR",
				title: "Move Already Added",
				body: "This simulated move is already in the sandbox",
			})
			return
		}

		const bookingForValidation = displayedBookingDemand.find((booking) => booking.bookingID === move.bookingID)
		const validation =
			move.validation ||
			getMoveValidation({
				booking: bookingForValidation,
				targetDay: move.toDay,
				eventConfig,
			})

		setStagedMoves((prev) => [
			...prev,
			{
				id: move.id || `${move.bookingID}-${move.fromDay}-${move.toDay}-${Date.now()}`,
				bookingID: move.bookingID,
				reference: move.reference,
				fromDay: move.fromDay,
				toDay: move.toDay,
				movedGuests: move.movedGuests || 0,
				valid: validation.valid,
				reasons: validation.reasons || [],
			},
		])
	}

	const stageManualMove = () => {
		if (!selectedBooking || !selectedFromDay || !selectedToDay) {
			pushAlert({
				type: "ERROR",
				title: "Incomplete Move",
				body: "Choose a booking, a source day, and a target day first",
			})
			return
		}

		addMoveToSimulation({
			bookingID: selectedBooking.bookingID,
			reference: selectedBooking.reference,
			fromDay: selectedFromDay,
			toDay: selectedToDay,
			movedGuests: selectedBooking.days[selectedFromDay] || 0,
		})

		setSelectedBookingID("")
		setSelectedFromDay("")
		setSelectedToDay("")
		setBookingSearch("")
	}

	const removeMove = (moveId) => {
		setStagedMoves((prev) => prev.filter((move) => move.id !== moveId))
	}

	const resetSimulation = () => {
		setStagedMoves([])
	}

	const saveInventory = async () => {
		if (!eventId) return

		try {
			setSavingInventory(true)
			const normalizedInventory = normalizeInventory(inventory)

			await db.doc(`events/${eventId}`).set(
				{
					ticket_exposure_inventory: {
						...normalizedInventory,
						updated_at: firebase.firestore.FieldValue.serverTimestamp(),
						updated_by: user?.uid || "",
					},
				},
				{ merge: true },
			)

			const updatedDoc = await db.doc(`events/${eventId}`).get()
			const updatedInventory = normalizeInventory(updatedDoc.data()?.ticket_exposure_inventory || normalizedInventory)

			setInventory(updatedInventory)
			setSavedInventoryState({
				values: updatedInventory,
				updatedAt: updatedDoc.data()?.ticket_exposure_inventory?.updated_at || null,
				updatedBy: updatedDoc.data()?.ticket_exposure_inventory?.updated_by || user?.uid || "",
			})

			pushAlert({
				type: "SUCCESS",
				title: "Inventory Saved",
				body: "Ticket exposure inventory saved successfully",
			})
		} catch (error) {
			console.error("Failed to save ticket exposure inventory:", error)
			pushAlert({
				type: "ERROR",
				title: "Save Failed",
				body: "Could not save the ticket exposure inventory",
			})
		} finally {
			setSavingInventory(false)
		}
	}

	return (
		<div style={{ display: "grid", gap: "24px" }}>
			<div style={{ display: "grid", gap: "14px", padding: "18px 20px", border: "1px solid #ececec", borderRadius: "12px", background: "#fafafa" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
					<div>
						<h4 style={{ margin: 0, marginBottom: "4px" }}>Inventory</h4>
						<small style={{ color: "#666666", display: "block" }}>{loadingInventory ? "Loading saved inventory..." : getInventoryNote(savedInventoryState)}</small>
					</div>
					{stagedMoves.length > 0 && (
						<Badge
							type="AMBER"
							label={`${stagedMoves.length} simulated move${stagedMoves.length === 1 ? "" : "s"}`}
						/>
					)}
				</div>

				<div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "nowrap", overflowX: "auto", paddingBottom: "4px" }}>
					{EXPOSURE_DAYS.map((day) => (
						<div
							key={day}
							style={{ minWidth: "124px", flex: "0 0 124px", display: "grid", gap: "6px" }}>
							<small style={{ color: "#666666", fontSize: "12px", lineHeight: 1.2 }}>{formatDayLabel(day)}</small>
							<Input
								type="number"
								placeholder="0"
								value={inventory[day]}
								onChange={(value) => setInventory((prev) => ({ ...prev, [day]: value }))}
							/>
						</div>
					))}
					<div style={{ flex: "0 0 auto", minWidth: "150px" }}>
						<Button
							label="Save inventory"
							onClick={saveInventory}
							loading={savingInventory}
							loadingText="Saving..."
							disabled={loadingInventory}
							fullWidth
						/>
					</div>
				</div>
			</div>

			<div style={{ display: "grid", gridTemplateColumns: "minmax(360px, 440px) minmax(0, 1fr)", gap: "16px", alignItems: "start" }}>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
					{exposureMetrics.map((metric) => {
						const cardStyle = getMetricCardStyle(metric.tone)

						return (
							<div
								key={metric.id}
								style={{
									padding: "16px 18px",
									border: cardStyle.border,
									borderRadius: "12px",
									background: cardStyle.background,
									boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
									minHeight: "108px",
									display: "grid",
									alignContent: "space-between",
									gap: "10px",
								}}>
								<div style={{ display: "grid", gap: "6px" }}>
									<small style={{ color: "#667085", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{metric.label}</small>
									<h3 style={{ margin: 0, fontSize: "30px", lineHeight: 1, color: cardStyle.accent }}>{metric.value}</h3>
								</div>
								<small style={{ color: "#667085", display: "block", lineHeight: 1.35, fontSize: "12px" }}>{metric.helper}</small>
							</div>
						)
					})}
				</div>

				<div style={{ minWidth: 0, width: "fit-content", maxWidth: "100%", justifySelf: "start", paddingLeft: "80px" }}>
					<Table
						className="bookings-table"
						headings={["Day", "Required", "Available", "Delta", "Status"]}>
						{EXPOSURE_DAYS.map((day) => {
							const dayExposure = displayedExposure[day]
							const badge = getStatusBadge(dayExposure)

							return (
								<tr key={day}>
									<td>{formatDayLabel(day)}</td>
									<td>{dayExposure.required}</td>
									<td>{dayExposure.available}</td>
									<td>{dayExposure.delta}</td>
									<td>
										<div style={{ width: "fit-content", justifySelf: "start" }}>
											<Badge
												type={badge.type}
												label={badge.label}
											/>
										</div>
									</td>
								</tr>
							)
						})}
					</Table>
				</div>
			</div>

			<div style={{ display: "grid", gap: "22px" }}>
				<div style={{ display: "grid", gap: "16px", padding: "18px 20px", border: "1px solid #ececec", borderRadius: "12px", background: "#fafafa", maxWidth: "860px" }}>
					<div style={{ display: "grid", gap: "6px" }}>
						<h4 style={{ margin: 0 }}>Rebalance Simulator</h4>
						<small style={{ color: "#666666" }}>Stage booking swaps to see the impact before sales edits the booking manually.</small>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 2.2fr) minmax(140px, 1fr) minmax(140px, 1fr) auto", gap: "10px", alignItems: "end" }}>
						<div ref={bookingSearchRef} style={{ position: "relative" }}>
							<Input
								type="text"
								placeholder="Choose booking"
								value={bookingSearch}
								onFocus={() => setShowBookingDropdown(true)}
								onChange={(value) => {
									setBookingSearch(value)
									setShowBookingDropdown(true)
									if (selectedBookingID) {
										setSelectedBookingID("")
										setSelectedFromDay("")
										setSelectedToDay("")
									}
								}}
							/>
							{showBookingDropdown && (
								<div
									style={{
										position: "absolute",
										top: "calc(100% + 6px)",
										left: 0,
										right: 0,
										zIndex: 20,
										maxHeight: "280px",
										overflowY: "auto",
										background: "#ffffff",
										border: "1px solid #e4e7ec",
										borderRadius: "10px",
										boxShadow: "0 12px 24px rgba(16, 24, 40, 0.12)",
									}}>
									{filteredBookingOptions.length > 0 ? (
										filteredBookingOptions.map((booking, index) => (
											<div
												key={booking.id}
												style={{
													padding: "11px 14px",
													cursor: "pointer",
													borderBottom: index === filteredBookingOptions.length - 1 ? "none" : "1px solid #f2f4f7",
													fontSize: "14px",
													lineHeight: 1.4,
												}}
												onMouseDown={(event) => {
													event.preventDefault()
													setSelectedBookingID(booking.id)
													setSelectedFromDay("")
													setSelectedToDay("")
													setBookingSearch(booking.label)
													setShowBookingDropdown(false)
												}}>
												{booking.label}
											</div>
										))
									) : (
										<div style={{ padding: "11px 14px", color: "#667085", fontSize: "14px" }}>
											No matching bookings
										</div>
									)}
								</div>
							)}
						</div>
						<Select
							selected={selectedFromDay}
							placeholder="From day"
							options={sourceDayOptions}
							onSelect={({ option }) => {
								setSelectedFromDay(option)
								setSelectedToDay("")
							}}
						/>
						<Select
							selected={selectedToDay}
							placeholder="To day"
							options={targetDayOptions}
							onSelect={({ option }) => setSelectedToDay(option)}
						/>
						<div style={{ display: "flex", gap: "10px" }}>
							<Button
								label="Add move"
								onClick={stageManualMove}
								disabled={!selectedBookingID || !selectedFromDay || !selectedToDay}
							/>
							{stagedMoves.length > 0 && (
								<Button
									label="Reset"
									type="SECONDARY"
									onClick={resetSimulation}
								/>
							)}
						</div>
					</div>

					{selectedBooking && selectedToDay && !selectedMoveValidation.valid && (
						<WarningPanel
							message="This move can be simulated, but it should not be suggested automatically."
							reasons={selectedMoveValidation.reasons}
						/>
					)}
				</div>

				<div style={{ display: "grid", gap: "16px" }}>
					<div>
						<h4 style={{ margin: 0, marginBottom: "8px" }}>Staged Moves</h4>
						<small style={{ color: "#666666", display: "block" }}>These changes are simulated only and have not been written back to any booking.</small>
					</div>

					<Table
						className="bookings-table"
						headings={["Booking", "Check In(s)", "Move", "Guests", "Warnings", ""]}>
						{stagedMoves.length > 0 ? (
							stagedMoves.map((move) => (
								<tr key={move.id}>
									<td>
										<div>
											{move.reference} - {bookingDetailsByID[move.bookingID]?.client?.name || "Unknown client"}
										</div>
									</td>
									<td>{getCheckInDatesString(bookingDetailsByID[move.bookingID]) || "-"}</td>
									<td>
										{formatDayLabel(move.fromDay)} to {formatDayLabel(move.toDay)}
									</td>
									<td>{move.movedGuests}</td>
									<td>{move.valid ? <small style={{ color: "#667085" }}>None</small> : <WarningPanel reasons={move.reasons} />}</td>
									<td style={{ textAlign: "right" }}>
										<Button
											label="Remove"
											type="SECONDARY"
											small
											onClick={() => removeMove(move.id)}
										/>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan={6}>
									<small>No simulated moves yet</small>
								</td>
							</tr>
						)}
					</Table>
				</div>
			</div>

			<div style={{ display: "grid", gap: "16px" }}>
				<div>
					<h4 style={{ margin: 0, marginBottom: "8px" }}>Suggested Moves</h4>
					<small style={{ color: "#666666", display: "block" }}>Best booking-level swaps based on the current exposure view, including any warned moves.</small>
				</div>

				<Table
					className="bookings-table"
					headings={["Booking", "Check In(s)", "Source", "Target", "Guests", "Warnings", ""]}>
					{suggestions.length > 0 ? (
						suggestions.map((suggestion) => {
							const alreadyStaged = stagedMoves.some((move) => move.bookingID === suggestion.bookingID && move.fromDay === suggestion.fromDay && move.toDay === suggestion.toDay)

							return (
								<tr key={suggestion.id}>
									<td>
										<div>
											{suggestion.reference} - {suggestion.clientName}
										</div>
									</td>
									<td>{suggestion.checkInDatesString || "-"}</td>
									<td>{formatDayLabel(suggestion.fromDay)}</td>
									<td>{formatDayLabel(suggestion.toDay)}</td>
									<td>{suggestion.movedGuests}</td>
									<td>
										{suggestion.validation?.valid ? (
											<small style={{ color: "#667085" }}>None</small>
										) : (
											<div>
												{(suggestion.validation?.reasons || []).map((reason) => (
													<small
														key={suggestion.id + reason.code + reason.message}
														style={{ display: "block", color: "#8a5b06" }}>
														{reason.message}
													</small>
												))}
											</div>
										)}
									</td>
									<td style={{ textAlign: "right" }}>
										<Button
											label={alreadyStaged ? "Added" : "Add to simulation"}
											type="SECONDARY"
											small
											disabled={alreadyStaged}
											onClick={() => addMoveToSimulation(suggestion)}
										/>
									</td>
								</tr>
							)
						})
					) : (
						<tr>
							<td colSpan={7}>
								<small>No beneficial swaps found from the current position</small>
							</td>
						</tr>
					)}
				</Table>
			</div>
		</div>
	)
}
